import React, { useMemo } from "react";
import * as XLSX from "xlsx";

export type ScrollableListProps = {
  /** Lista de cadenas a mostrar. */
  items: string[];
  /** Título mostrado arriba de la lista. Puede traer HTML; se sanitiza. */
  title?: string;
  /** Alto del contenedor con scroll (px). */
  height?: number;
  /** Si true usa <ol> (numerada); si false usa <ul> (viñetas). */
  numbered?: boolean;
  /** Nombre base del archivo de exportación. */
  exportFilename?: string;
  /** Si true (default), elimina etiquetas HTML de los items y el título. */
  sanitizeHtml?: boolean;
};

const ScrollableListWithExport: React.FC<ScrollableListProps> = ({
  items,
  title = "Lista",
  height = 240,
  numbered = true,
  exportFilename,
  sanitizeHtml = true,
}) => {
  // Sanitizar HTML -> texto plano
  const stripHtml = (s?: string) => {
    if (!s) return "";
    try {
      const doc = new DOMParser().parseFromString(String(s), "text/html");
      return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
    } catch {
      return String(s).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }
  };

  const safeTitle = sanitizeHtml ? stripHtml(title) : (title || "");

  const data = useMemo(() => {
    const base = Array.isArray(items) ? items : [];
    return base
      .map((v) => (sanitizeHtml ? stripHtml(v) : String(v ?? "")))
      .filter((v) => v !== "");
  }, [items, sanitizeHtml]);

  // Nombre de archivo limpio y sin acentos
  const fileBase = (exportFilename || safeTitle || "lista")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9 _-]/g, "")
    .trim() || "lista";

  const exportExcel = () => {
    const rows = data.map((Valor, i) => ({ "#": i + 1, Valor }));
    const ws = XLSX.utils.json_to_sheet(rows);

    // Ajuste básico de ancho de columnas
    const anchoValor = Math.max(10, Math.min(80, Math.max(...rows.map((r) => String(r.Valor).length), 10)));
    (ws as any)["!cols"] = [{ wch: 5 }, { wch: anchoValor }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lista");
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  return (
    <div className="card" style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,.08)" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h5 className="m-0">{safeTitle}</h5>
            <small className="text-muted">{data.length} ítems</small>
          </div>
          <button className="btn btn-sm btn-primary" onClick={exportExcel} title="Exportar a Excel">
            Exportar Excel
          </button>
        </div>

        <div
          style={{
            height,
            overflowY: "auto",
            border: "1px solid rgba(0,0,0,.1)",
            borderRadius: 8,
            padding: "8px 12px",
          }}
        >
          {numbered ? (
            <ol className="m-0" style={{ paddingLeft: "1.25rem" }}>
              {data.map((txt, idx) => (
                <li key={idx} className="py-1">
                  {txt}
                </li>
              ))}
            </ol>
          ) : (
            <ul className="m-0" style={{ paddingLeft: "1.25rem" }}>
              {data.map((txt, idx) => (
                <li key={idx} className="py-1">
                  {txt}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrollableListWithExport;
